/*
 * The two halves of "who is signed in", in one place: the session holds the tokens, and
 * localStorage holds a copy of them so a new tab starts signed in.
 *
 * There used to be two half-logouts. `clearTokens` in axiosConfig cleared the session and the
 * localStorage copies; `logoutFromHelper` cleared only the session. Since App.js restores the
 * session from those copies on every load whenever `kinesin-isAuthenticated` is "true", the
 * second one left enough behind to resurrect a dead token: you were bounced to the login
 * screen, the next load put the expired token back, whoami failed, and you were bounced again.
 *
 * So the flag matters as much as the tokens — clearing the tokens while leaving it "true" makes
 * the restore write `null` back into the session, and a stringified "null" reads as a token to
 * anything checking only for presence.
 *
 * No imports on purpose: both the axios layer and the redux-aware helper call this, and either
 * direction of import between them would be a cycle.
 */
const SESSION_KEYS = ["accessToken", "refreshToken", "userId", "requestId"];

const localKeyFor = (key) => `kinesin-${key}`;

const AUTHENTICATED_FLAG = "kinesin-isAuthenticated";

export const clearSession = () => {
  SESSION_KEYS.forEach((key) => {
    sessionStorage.removeItem(key);
    /*
     * The practice tenant is deliberately left alone: it identifies which surgery this browser
     * belongs to rather than who is signed in, and the login screen needs it.
     */
    localStorage.removeItem(localKeyFor(key));
  });
  localStorage.setItem(AUTHENTICATED_FLAG, "false");
};

/*
 * Restoring is all-or-nothing on the access token. A flag left at "true" next to a missing copy
 * is the half state above, and carrying it into the session is what produced the loop; treating
 * it as signed out instead means the login screen loads with nothing to send.
 */
export const restoreSession = () => {
  if (localStorage.getItem(AUTHENTICATED_FLAG) !== "true") return;

  if (!localStorage.getItem(localKeyFor("accessToken"))) {
    clearSession();

    return;
  }

  SESSION_KEYS.forEach((key) => {
    const stored = localStorage.getItem(localKeyFor(key));
    if (stored) sessionStorage.setItem(key, stored);
  });
};
